// ── THE REASONS — why a row is in a list, said the way the office says it ───
// His law of every list (2026-10-27): the row says WHY it is in the list, in a
// sentence a person says; the user filters on those reasons; « Tous » shows
// the reasons one block after another, never a merged clock; a reason is
// never demoted to a row mark.
//
// ★ A REASON IS A LANE PLUS ITS SERVER LEG — the WHOLE lane: a renewal's child
// is simply a to_produce / to_contact / proposals row whose job renews one
// (prefixed « Renouvellement · » on its line). The rows, verbs and doors come
// from the lane (lib/salesFlow); this table only says how the lists are cut
// and which PAGE each reason belongs to.
// ★ THE ORDER IS THE PRIORITY. Ventes opens on the price owed, then the
// requests, then what is in their hands. The landing rule reads this table in
// order.
// ⚰ The TAB/STAGE layer (« Soumissions › À écrire ») is gone: a tab was a
// second axis over the same four lists, and a stage menu that forbade « Tous »
// made the desk a place you could not read as a whole. The renewal campaign
// is its own page (lib/renewalCampaign) — a cohort of CUSTOMERS, never a queue.
import type { SalesFlowMeta, SalesGroupId, SalesThread } from '@/lib/salesFlow'

export const SALES_PAGES = ['sales', 'payments'] as const
export type SalesPageId = (typeof SALES_PAGES)[number]

// ★ IN THE DEAL'S ORDER (his ruling): a request to evaluate, a quote to
// write, a quote out — then the bill. The order is also where you LAND (the
// first reason with work) and the block order under « Tous ».
// « Paiement avant travaux » leads Paiements: work is WAITING on that money,
// where an ordinary unpaid bill is work already done.
export const SALES_REASON_IDS = ['to_assess', 'to_write', 'sent', 'prepay', 'unpaid'] as const
export type SalesReasonId = (typeof SALES_REASON_IDS)[number]

/** The server flag a reason's list runs under (jobs#index), one each — the
 *  SAME predicate the rollup counts for it (the parity spec). */
export type SalesReasonLeg = 'new_requests' | 'paper_owed' | 'unanswered' | 'unpaid' | 'payment_held'

export interface SalesReason {
  id: SalesReasonId
  page: SalesPageId
  /** The lane its rows wear — which decides the row's words and verbs. */
  group: SalesGroupId
  leg: SalesReasonLeg
}

export const SALES_REASONS: readonly SalesReason[] = [
  { id: 'to_assess', page: 'sales', group: 'to_contact', leg: 'new_requests' },
  { id: 'to_write', page: 'sales', group: 'to_produce', leg: 'paper_owed' },
  { id: 'sent', page: 'sales', group: 'proposals', leg: 'unanswered' },
  { id: 'prepay', page: 'payments', group: 'prepay', leg: 'payment_held' },
  { id: 'unpaid', page: 'payments', group: 'invoices', leg: 'unpaid' },
]

const REASON_SET: ReadonlySet<string> = new Set(SALES_REASON_IDS)
// A URL is user input: the predicates read an unknown value as « none ».
export const isSalesReason = (value: string): value is SalesReasonId => REASON_SET.has(value)

const REASON_BY_ID = new Map(SALES_REASONS.map(reason => [ reason.id, reason ]))
export const reasonById = (id: SalesReasonId): SalesReason => REASON_BY_ID.get(id) ?? SALES_REASONS[0]
/** A page's reasons, in priority order; `null` = every reason (the phone's
 *  one screen). */
export const reasonsOf = (page: SalesPageId | null): SalesReason[] =>
  page ? SALES_REASONS.filter(reason => reason.page === page) : [ ...SALES_REASONS ]

/** The list you stand in: ONE reason, or every reason of the page one block
 *  after another (« Tous »). */
export type SalesList = SalesReasonId | 'all'
export const isSalesList = (value: string): value is SalesList => value === 'all' || isSalesReason(value)

// ── The season lens ─────────────────────────────────────────────────────────
// ★ WHICH REASONS THE OPEN-SEASON SCOPE FENCES (his ruling): the ones the
// OFFICE holds — what the shop owes today is a matter of which seasons are
// open. A quote out with the customer is owed whatever the year, so it takes
// the NAMED runs only when the user picks one; a bill takes no season at all.
// One predicate for the desk, the phone sheet and the composable's legs.
export const SEASON_SCOPED_REASONS: ReadonlySet<SalesReasonId> = new Set<SalesReasonId>(['to_write', 'to_assess'])
export const seasonScoped = (reason: SalesReasonId | null): boolean => !!reason && SEASON_SCOPED_REASONS.has(reason)
/** Whether the season CONTROL is drawn for a list: everywhere but
 *  « Impayées » — a pick is a pick on every sales reason, « Soumission non
 *  signée » included. */
// Money takes no season — and with two money reasons, Paiements' « Tous » is
// money too, so the page decides it as a whole.
export const seasonControlDrawn = (list: SalesList | null, page: SalesPageId | null = null): boolean =>
  page !== 'payments' && list !== 'unpaid' && list !== 'prepay'

// ── The counts ──────────────────────────────────────────────────────────────
// Whole-population server meta, NEVER rows.length: the list is capped on
// purpose, so counting what is rendered would understate every reason.
// ★ A REASON'S COUNT IS ITS OWN LIST'S PREDICATE — the rollup serves one per
// reason, each the index request its list runs (the parity spec holds both
// sides). « Tous » is the sum of the page's reasons, the only sum that cannot
// disagree with anything drawn under it.
export function reasonCount(meta: SalesFlowMeta | null, id: SalesReasonId): number | null {
  if (!meta) return null
  switch (id) {
    // Each the WHOLE lane, renewal children included.
    case 'to_write': return meta.paper_owed.count
    case 'to_assess': return meta.leads.count
    case 'sent': return meta.unanswered.count
    // ⚠ NOT `invoices`: that counts every outstanding bill (incl. one written
    // off). This reason counts the lane's exact JOB population.
    case 'unpaid': return meta.to_collect.count
    // Partitioned from `unpaid` server-side — the two never count one job twice.
    case 'prepay': return meta.to_prepay.count
  }
}

export function listCount(meta: SalesFlowMeta | null, page: SalesPageId | null, list: SalesList): number | null {
  if (!meta) return null
  if (list !== 'all') return reasonCount(meta, list)
  return reasonsOf(page).reduce((sum, reason) => sum + (reasonCount(meta, reason.id) ?? 0), 0)
}

export interface ReasonEntry {
  list: SalesList
  /** null until the rollup lands — the slot is drawn, the number is awaited. */
  count: number | null
}

/** The menu: « Tous » first, then the page's reasons in priority order —
 *  EVERY entry always drawn (a slot that disappears moves the furniture; one
 *  that reads 0 teaches nothing and stays put). A single-reason page has no
 *  menu, so its entries carry no « Tous ». */
export function menuEntries(meta: SalesFlowMeta | null, page: SalesPageId | null): ReasonEntry[] {
  const reasons = reasonsOf(page)
  const own: ReasonEntry[] = reasons.map(reason => ({ list: reason.id, count: reasonCount(meta, reason.id) }))
  return reasons.length > 1 ? [ { list: 'all', count: listCount(meta, page, 'all') }, ...own ] : own
}

/** The sidebar's number for a page = the page's « Tous » — every reason,
 *  a quote out and a bill out included (his ruling: a soumission unsigned is a
 *  relance owed, an invoice unpaid a call; and every row can be CLEARED, by
 *  its verb or by « Reporter », so the number can reach zero). ⚰ The
 *  office-held-only badge (« au client » reasons left out) is gone. */
export function pageBadge(meta: SalesFlowMeta | null, page: SalesPageId): number | null {
  return listCount(meta, page, 'all')
}

// ── Where you land ──────────────────────────────────────────────────────────
// ★ DECIDED ONCE, FROM THE COUNTS, NEVER FROM WHO ANSWERED FIRST. Taking the
// first populated list the moment any signal arrived made the landing a RACE
// between the legs — the same desk opened twice landed on different lists
// (his QA). The rollup counts every reason in one answer, so it is the only
// signal that can be read in order: the first reason with work, else « Tous »
// (a page with nothing owed still has a place to stand, and it says so). The
// loaded-rows fallback is for a rollup that FAILED, and waits for every list
// to settle before it guesses. Returns null while nothing can be decided yet
// (the caller keeps watching).
export function landingReason(
  meta: SalesFlowMeta | null, page: SalesPageId | null, loaded: readonly SalesReasonId[], busy: boolean,
): SalesList | null {
  const reasons = reasonsOf(page)
  if (meta) return reasons.find(reason => (reasonCount(meta, reason.id) ?? 0) > 0)?.id ?? 'all'
  if (busy) return null
  return loaded.find(id => !page || reasonById(id).page === page) ?? 'all'
}

// ── The row's line ──────────────────────────────────────────────────────────
// ★ THE ROW SAYS WHY IT IS IN THE LIST, in the office's voice, with the clock
// that reason is owed on: « Soumission à écrire · évaluée il y a 12 j ». ONE
// derivation for the desk row and the phone row (the two cannot drift), read
// off the thread's lane and state: the lane is the reason, a started sheet
// turns « à écrire » into « écrite, pas envoyée », and a request for a
// DESK-PRICED type (`typeEvaluationByDefault === false`) owes a quote, not a
// look-see — « Soumission à écrire · reçue il y a N j » — the same fork
// lib/salesFlow `rowState` makes. A renewal's child is prefixed
// « Renouvellement · » by the row (`renewalOf`). The campaign's rows
// (`to_renew`) have their own line — null here.
export interface ReasonLine {
  /** A key taking the day count as `{n}`. */
  key: string
  count: number
}

export function reasonLine(thread: Pick<SalesThread, 'group' | 'proposalId' | 'invoiceId' | 'dueDays' | 'typeEvaluationByDefault'>): ReasonLine | null {
  const count = thread.dueDays
  switch (thread.group) {
    case 'to_produce': return { key: thread.proposalId ? 'salesFlow.reasonLine.written' : 'salesFlow.reasonLine.toWrite', count }
    case 'to_contact':
      if (thread.proposalId) return { key: 'salesFlow.reasonLine.written', count }
      return { key: thread.typeEvaluationByDefault === false ? 'salesFlow.reasonLine.toWriteNew' : 'salesFlow.reasonLine.toAssess', count }
    case 'proposals': return { key: 'salesFlow.reasonLine.sent', count }
    case 'invoices': return { key: 'salesFlow.reasonLine.unpaid', count }
    // A bill out is awaited; before it, the bill is the office's to cut.
    case 'prepay': return { key: thread.invoiceId ? 'salesFlow.reasonLine.prepayAwaiting' : 'salesFlow.reasonLine.prepayToBill', count }
    case 'to_renew': return null
  }
}
